import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import { Modal, Button, Spin, message } from "antd";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

const PulseOXChart = ({ smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [chartData1D, setChartData1D] = useState([]);
  const [chartData7D, setChartData7D] = useState([]);
  const [loading, setLoading] = useState(false);
  const [start7D, setStart7D] = useState(dayjs().subtract(6, "day"));
  const [end7D, setEnd7D] = useState(dayjs());
  const dayStart = selectedDate.startOf("day").valueOf();
  const dayEnd = selectedDate.add(1, "day").startOf("day").valueOf();

  const fourHourTicks = Array.from({ length: 7 }, (_, i) =>
    dayjs(dayStart)
      .add(i * 4, "hour")
      .valueOf(),
  );
  
  const fetch1DData = async (date) => {
    if (!smartWatchId) return;

    const formattedDate = dayjs(date).format("DD/MM/YYYY");
    const payload = {
      type: "PULSEOX",
      userId: smartWatchId,
      fromDate: formattedDate,
      toDate: formattedDate,
      limit: 1,
      offset: 0,
    };

    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);

      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        const samples = raw?.timeOffsetSpo2Values || {};

        const data = Object.entries(samples)
          .map(([offset, value]) => {
            const time = dayjs(date)
              .startOf("day")
              .add(Number(offset), "second")
              .valueOf();

            return { time, value };
          })
          .sort((a, b) => a.time - b.time);

        setChartData1D(data);
      } else {
        setChartData1D([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetch7DData = async (startDate, endDate) => {
    if (!smartWatchId) return;
    const start = dayjs(startDate).format("DD/MM/YYYY");
    const end = dayjs(endDate).format("DD/MM/YYYY");
    const payload = {
      type: "PULSEOX",
      userId: smartWatchId,
      fromDate: start,
      toDate: end,
      limit: 100,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);

      if (res?.data?.length > 0) {
        const data = res.data
          .sort((a, b) => a.CALENDAR_DATE - b.CALENDAR_DATE)
          .map((item) => ({
            date: dayjs(item.CALENDAR_DATE).local().format("DD/MM/YYYY"),
            value: Number(item.Value),
          }));

        setChartData7D(data);
      } else setChartData7D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
    } else {
      fetch7DData(start7D, end7D);
    }
  }, [tab, selectedDate, smartWatchId, start7D, end7D]);

  const handlePrevDay = () => setSelectedDate((d) => d.subtract(1, "day"));

  const handlePrev7D = () => {
    const newStart = start7D.subtract(7, "day");
    const newEnd = end7D.subtract(7, "day");
    setStart7D(newStart);
    setEnd7D(newEnd);
  };
  const handleNextDay = () => {
    setSelectedDate((d) => {
      const nextDay = d.add(1, "day");

      if (nextDay.isAfter(dayjs(), "day")) {
        message.info("Data for future dates will be available tomorrow.");
        return d;
      }

      return nextDay;
    });
  };

  const handleNext7D = () => {
    const today = dayjs();

    let newStart = start7D.add(7, "day");
    let newEnd = end7D.add(7, "day");

    if (newEnd.isAfter(today, "day")) {
      message.info("Data for future dates will be available tomorrow.");

      newEnd = today;
      newStart = today.subtract(6, "day");
    }

    setStart7D(newStart);
    setEnd7D(newEnd);
  };

  return (
    <div>
      <div className="flex justify-center mb-4 gap-3">
        <button
          onClick={() => setTab("1D")}
          className={`${
            tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
          } px-4 py-2 rounded-md`}
        >
          1D
        </button>
        <button
          className={`${
            tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
          } px-4 py-2 rounded-md`}
          onClick={() => setTab("7D")}
        >
          7D
        </button>
      </div>

      {loading && <Loader />}

      {tab === "1D" && (
        <>
          <div className="flex items-center justify-center gap-4 mb-4">
            <Button onClick={handlePrevDay}>← Prev Day</Button>
            <span className="font-semibold">
              {selectedDate.format("DD/MM/YYYY")}
            </span>
            <Button onClick={handleNextDay}>Next Day →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData1D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  type="number"
                  scale="time"
                  domain={[dayStart, dayEnd]}
                  ticks={fourHourTicks}
                  tickFormatter={(t) => dayjs(t).format("h A")}
                />

                <YAxis />
                <Tooltip labelFormatter={(t) => dayjs(t).format("hh:mm A")} />

                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#66FF66"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {tab === "7D" && (
        <>
          <div className="flex justify-between items-center mb-4">
            <Button onClick={handlePrev7D}>← Prev 7 Days</Button>

            <span>
              {start7D.format("MMM D")} - {end7D.format("MMM D")}
            </span>

            <Button onClick={handleNext7D}>Next 7 Days →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData7D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default PulseOXChart;
